(function () {
  var enterModule = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.enterModule : undefined;
  enterModule && enterModule(module);
})();

var __signature__ = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.default.signature : function (a) {
  return a;
};

import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { escapeStringRegexp } from "../../../../utils/regexp";
import Input from "../../../../components/Input";
import SelectCountry from "../SelectCountry";
import SearchHistory from "../SearchHistory";
import styles from "./SearchBox.module.css";
import OutsideClick from "../../../../components/OutsideClick";
import { Search } from "../../../../icons/Search";

const SearchBox = props => {
  const {
    searchString,
    countries,
    countryCode,
    onSearch,
    findHistory,
    searchHistory,
    withCountrySelect,
    languageDir,
    onChangeCountry
  } = props;
  const {
    t
  } = useTranslation();
  const [isShowHistory, setIsShowHistory] = useState(false);
  const [isOnFocus, setIsOnFocus] = useState(false);
  const [searchValue, setSearchValue] = useState(searchString ? searchString.trim() : "");
  const [country, setCountry] = useState(countries.find(_ref => {
    let {
      code
    } = _ref;
    return code === countryCode;
  }) || countries[0]);
  const location = useLocation();
  const isSearchRoute = useMemo(() => {
    var _location$pathname;

    return !!(location !== null && location !== void 0 && (_location$pathname = location.pathname) !== null && _location$pathname !== void 0 && _location$pathname.match("search"));
  }, [location]);
  const canSubmit = searchValue && country;
  const onSearchChange = useCallback(event => {
    setSearchValue(event.target.value);
  }, []);
  const handleCountryChange = useCallback(value => {
    setCountry(value);
    setIsOnFocus(true);
    if (onChangeCountry) onChangeCountry(value);
  }, [setCountry, onChangeCountry]);
  const onClick = useCallback(() => {
    setIsShowHistory(true);
    findHistory();
  }, [findHistory]);
  const onSearchByHistory = useCallback(_ref2 => {
    let {
      query,
      country: code
    } = _ref2;
    setSearchValue(query);
    setCountry(countries.find(c => c.code === code));
    onSearch({
      searchString: query,
      countryCode: code
    });
  }, [countries, onSearch]); // eslint-disable-next-line

  const onFocus = useCallback(() => {
    setIsOnFocus(true);
  }); // eslint-disable-next-line

  const onClickOutside = useCallback(event => {
    setIsOnFocus(false);
  });
  const onSubmit = useCallback(event => {
    event.preventDefault();
    const q = searchValue ? searchValue.trim() : "";
    setSearchValue(q);
    onSearch({
      searchString: q,
      countryCode: country.code
    });
  }, [onSearch, searchValue, country.code]);
  const onCloseHistory = useCallback(() => setIsShowHistory(false), [setIsShowHistory]);
  const filteredSearchHistory = useMemo(() => searchHistory.filter(h => new RegExp(escapeStringRegexp(searchValue.toLowerCase())).test(h.query.toLowerCase())), [searchHistory, searchValue]);
  const inputSize = "lg";
  const isRtl = languageDir === "rtl";
  const selectCountryBorderRadius = useMemo(() => isRtl ? "4px 0 0 ".concat(isShowHistory ? "0" : "4px") : "0 4px ".concat(isShowHistory ? "0" : "4px", " 0"), [isRtl, isShowHistory]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.searchBoxContainer
  }, /*#__PURE__*/React.createElement(OutsideClick, {
    onClick: onClickOutside
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: onSubmit,
    className: styles.searchBox
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.searchInput
  }, /*#__PURE__*/React.createElement(Input, {
    isRtl: isRtl,
    role: "searchbox",
    placeholder: t("Search by commodity commercial description or HS code"),
    className: cx(styles.input, {
      [styles.inputWithHistory]: isShowHistory,
      [styles.inputWithSelect]: withCountrySelect,
      [styles.inputWithoutSelect]: !withCountrySelect,
      // [styles.withSubmit]: isOnFocus && canSubmit,
      [styles.rtl]: isRtl
    }),
    icon: /*#__PURE__*/React.createElement(Search, null),
    size: inputSize,
    value: searchValue,
    onChange: onSearchChange,
    onClick: onClick,
    onFocus: onFocus
  })), withCountrySelect && /*#__PURE__*/React.createElement(SelectCountry, {
    blurInputOnSelect: true,
    borderRadius: selectCountryBorderRadius,
    countries: countries,
    className: cx(styles.countrySelect, {
      [styles.rtl]: isRtl,
      [styles.selectWithHistory]: isShowHistory
    }),
    onChange: handleCountryChange,
    value: country,
    size: inputSize,
    isRtl: isRtl,
    role: "option"
  })), isShowHistory && filteredSearchHistory.length > 0 && /*#__PURE__*/React.createElement(SearchHistory, {
    history: filteredSearchHistory,
    withCountry: withCountrySelect,
    onClose: onCloseHistory,
    onSearch: onSearchByHistory
  })));
};

__signature__(SearchBox, "useTranslation{{ t }}\nuseState{[isShowHistory, setIsShowHistory](false)}\nuseState{[isOnFocus, setIsOnFocus](false)}\nuseState{[searchValue, setSearchValue](searchString ? searchString.trim() : \"\")}\nuseState{[country, setCountry](countries.find(({ code }) => code === countryCode) || countries[0])}\nuseLocation{location}\nuseMemo{isSearchRoute}\nuseCallback{onSearchChange}\nuseCallback{handleCountryChange}\nuseCallback{onClick}\nuseCallback{onSearchByHistory}\nuseCallback{onFocus}\nuseCallback{onClickOutside}\nuseCallback{onSubmit}\nuseCallback{onCloseHistory}\nuseMemo{filteredSearchHistory}\nuseMemo{selectCountryBorderRadius}", () => [useTranslation, useLocation]);

SearchBox.propTypes = {
  countries: PropTypes.array.isRequired,
  countryCode: PropTypes.string,
  searchString: PropTypes.string,
  searchHistory: PropTypes.array.isRequired,
  findHistory: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  withCountrySelect: PropTypes.bool,
  languageDir: PropTypes.string.isRequired,
  onChangeCountry: PropTypes.func
};
SearchBox.defaultProps = {
  searchString: "",
  countryCode: "",
  withCountrySelect: true,
  onChangeCountry: null
};

const _default = /*#__PURE__*/React.memo(SearchBox);

export default _default;
;

(function () {
  var reactHotLoader = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.default : undefined;

  if (!reactHotLoader) {
    return;
  }

  reactHotLoader.register(SearchBox, "SearchBox", "/root/local/TeamCity/buildAgent/work/bac1af6cbd11267e/src/modules/Search/components/SearchBox/SearchBox.jsx");
  reactHotLoader.register(_default, "default", "/root/local/TeamCity/buildAgent/work/bac1af6cbd11267e/src/modules/Search/components/SearchBox/SearchBox.jsx");
})();

;

(function () {
  var leaveModule = typeof reactHotLoaderGlobal !== 'undefined' ? reactHotLoaderGlobal.leaveModule : undefined;
  leaveModule && leaveModule(module);
})();